import { Link, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Menu, X, Instagram, BookOpen } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/hooks/useSettings";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/boutique", label: "Boutique" },
  { to: "/pack", label: "Le Pack" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const { count } = useCart();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-50 bg-sand/95 backdrop-blur-sm border-b border-line" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <BookOpen className="h-5 w-5 text-terracotta" />
          <span className="font-heading text-lg sm:text-xl text-ink tracking-tight">Kit Parental</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="text-sm text-ink-muted hover:text-terracotta transition-colors" data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/panier" className="relative p-2 rounded-md hover:bg-line/60 transition-colors" data-testid="cart-icon">
            <ShoppingBag className="h-5 w-5 text-ink" />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-white text-[10px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>}
          </Link>
          <button onClick={() => nav(user ? "/compte" : "/connexion")} className="p-2 rounded-md hover:bg-line/60 transition-colors" data-testid="account-icon">
            <User className="h-5 w-5 text-ink" />
          </button>
          <Button asChild className="hidden sm:inline-flex bg-terracotta hover:bg-terracotta-dark text-white rounded-md" data-testid="header-buy-pack">
            <Link to="/pack">Acheter le pack</Link>
          </Button>
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-line bg-sand px-5 py-4 space-y-3" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-ink-muted hover:text-terracotta">{n.label}</Link>
          ))}
          <Button asChild className="w-full bg-terracotta hover:bg-terracotta-dark text-white"><Link to="/pack" onClick={() => setOpen(false)}>Acheter le pack</Link></Button>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { data: s } = useSettings();
  const social = s?.social || {};
  return (
    <footer className="mt-24 bg-ink text-sand/80" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-5 w-5 text-terracotta-light" />
            <span className="font-heading text-xl text-sand">Kit Parental</span>
          </div>
          <p className="text-sm leading-relaxed max-w-sm text-sand/70">
            Des guides numériques bienveillants pour accompagner votre enfant de 0 à 6 ans. Accès immédiat, lecture sur tous vos appareils.
          </p>
          <div className="flex gap-3 mt-5">
            {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" className="hover:text-terracotta-light" data-testid="social-instagram"><Instagram className="h-5 w-5" /></a>}
          </div>
        </div>
        <div>
          <p className="font-overline text-sand/50 mb-4">Navigation</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/boutique" className="hover:text-terracotta-light">Boutique</Link></li>
            <li><Link to="/pack" className="hover:text-terracotta-light">Le Pack</Link></li>
            <li><Link to="/faq" className="hover:text-terracotta-light">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-terracotta-light">Contact</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-overline text-sand/50 mb-4">Informations</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/mentions-legales" className="hover:text-terracotta-light">Mentions légales</Link></li>
            <li><Link to="/cgv" className="hover:text-terracotta-light">CGV</Link></li>
            <li><Link to="/confidentialite" className="hover:text-terracotta-light">Confidentialité</Link></li>
            <li><Link to="/cookies" className="hover:text-terracotta-light">Cookies</Link></li>
            <li><Link to="/remboursement" className="hover:text-terracotta-light">Remboursement</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-sand/50">
        © {new Date().getFullYear()} Kit Parental — Produits numériques (eBooks PDF). Aucun produit physique n'est expédié.
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
